import { acceptHMRUpdate, defineStore } from 'pinia'
import { ErrorCodes, type BaseError, MetaErrorCodes, type Meta } from '@cplug/frontend-core/models'
import { BaseCRUDStore } from '@cplug/frontend-core/stores'
import type { Company } from '~/domain/shared/company/models/Company'
import { CPCompanyGateway } from '~/domain/shared/company/gateways/CPCompanyGateway'
import { DocumentType } from '~/domain/shared/document/models/Document'
import { useValidateAddress } from '~/domain/shared/address/composables/useAddressValidation'
import { useDocumentCNPJIsValid } from '~/shared/composables/useDocumentCNPJIsValid'
import { useErrorFormaterByMeta } from '~/shared/composables/useErrorFormatter'

class CompanyStore extends BaseCRUDStore<Company> {
  protected gateway = CPCompanyGateway.instance()

  protected findErrors (model: Company): BaseError[] {
    const defaultErrors = (meta: Meta): BaseError => ({ code: ErrorCodes.DataValidation, meta })
    const errors: BaseError[] = []

    // Validação para razão social (obrigatório)
    if ((model.companyName?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ companyName: MetaErrorCodes.Empty }))
    }

    if ((model.companyName?.trim().length ?? 0) > 200) {
      errors.push(defaultErrors({ companyName: MetaErrorCodes.LimitExceeded }))
    }

    // Validação para nome fantasia (obrigatório)
    if ((model.name?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ name: MetaErrorCodes.Empty }))
    }

    if (model.typeOfBusiness == null) {
      errors.push(defaultErrors({ type_of_business: MetaErrorCodes.Empty }))
    }

    if ((model.name?.trim().length ?? 0) > 120) {
      errors.push(defaultErrors({ name: MetaErrorCodes.LimitExceeded }))
    }

    // Validação para CNPJ (obrigatório)
    const cnpjDocument = model.documents?.find(doc => doc.type === DocumentType.CNPJ)

    if (cnpjDocument?.value && !useDocumentCNPJIsValid(cnpjDocument.value)) {
      errors.push(defaultErrors({ cnpj: MetaErrorCodes.WrongFormat }))
    }

    if ((cnpjDocument?.value?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ cnpj: MetaErrorCodes.Empty }))
    }

    // Validação para código da empresa (se preenchido)
    if (model.code && model.code.trim().length > 50) {
      errors.push(defaultErrors({ code: MetaErrorCodes.LimitExceeded }))
    }

    const addressErrors: Array<Meta | undefined> = []

    if (model.addresses?.length === 0) {
      errors.push(defaultErrors({ addresses: MetaErrorCodes.Empty }))
    }

    model.addresses?.forEach((companyAddress) => {
      const address = companyAddress.address
      const metaError = useValidateAddress(address)

      addressErrors.push(metaError)
    })

    if (addressErrors.filter(it => it != null).length > 0) {
      errors.push(defaultErrors({ addresses: addressErrors }))
    }

    if ((model.financialContact?.name?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ 'financial_contact.name': MetaErrorCodes.Empty }))
    }

    if ((model.ownerContact?.name?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ 'owner_contact.name': MetaErrorCodes.Empty }))
    }

    if (model.contacts.length === 0 || model.contacts[0].value.trim().length <= 0) {
      errors.push(defaultErrors({ 'contacts.0.value': MetaErrorCodes.Empty }))
    }

    return errors
  }
}

const useCompanyStore = defineStore('company', () => {
  const companyStore = new CompanyStore()
  const crudStore = companyStore.defaultStore()

  async function updateCompanyLogo (company: Company, savedCompany: Company): Promise<void> {
    if (company.logoBase64 == null || company.logoBase64 === '' || savedCompany.id == null) {
      return
    }

    const result = await CPCompanyGateway.instance().updatePhoto(savedCompany.id, company.logoBase64)
    result.ifError(splitErrors)
  }

  async function handlePostSaveOperations (
    company: Company,
    savedCompany: Company,
    originalLogoBase64: string | undefined
  ): Promise<void> {
    const operations: Promise<void>[] = []

    if (company.logoBase64 !== originalLogoBase64) {
      operations.push(updateCompanyLogo(company, savedCompany))
    }

    await Promise.all(operations)
  }

  async function save (company: Company, key: keyof Company = 'id'): Promise<boolean> {
    const originalLogoBase64 = crudStore.model.value?.logoBase64
    const callback = (savedCompany: Company): Promise<void> =>
      handlePostSaveOperations(company, savedCompany, originalLogoBase64)
    return await crudStore.save(company, callback, key)
  }

  function splitErrors (error: BaseError): void {
    crudStore.errors.value = useErrorFormaterByMeta(error)

    if (crudStore.errors.value.length === 0) {
      crudStore.errors.value = [error]
    }
  }

  async function revokeAppAccess (companyId: number, password: string): Promise<boolean> {
    if (companyId == null) {
      return false
    }

    const result = await CPCompanyGateway.instance().revokeAppAccess(companyId, password)

    return result.isOk()
  }

  async function getApiToken (companyId: number, password: string): Promise<string | null> {
    if (companyId == null) {
      return null
    }

    const result = await CPCompanyGateway.instance().getApiToken(companyId, password)

    return result.isOk() ? result.getValue() : null
  }

  return { ...crudStore, save, splitErrors, revokeAppAccess, getApiToken }
})

export const useCompanyStoreForHeader = defineStore('company/header', () => {
  const store = new CompanyStore()
  return { ...store.defaultStore() }
})

export type CompanyStoreType = ReturnType<typeof useCompanyStore>
export type CompanyStoreForHeaderType = ReturnType<typeof useCompanyStoreForHeader>

export default useCompanyStore

/* c8 ignore next 6 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useCompanyStore, (import.meta as any).hot))
  ;(import.meta as any).hot.accept(acceptHMRUpdate(useCompanyStoreForHeader, (import.meta as any).hot))
}
